import {
  presignResponseSchema,
  PRESIGN_MAX_BATCH,
  UPLOAD_BUCKET,
  type ListResult,
  type PresignResponse,
} from '../model';
import { postList } from './http';

// Storage Service client — presigned direct-to-object-storage uploads.
// See `model/upload.ts` for the three-legged flow and why step 2 bypasses the
// shared `http` client.

/**
 * Where an upload lands in the bucket. Keeps callers off magic strings — the
 * gateway accepts EXACTLY these three (2026-08-18 contract, note the singular
 * `product`/`restaurant`) and rejects anything else; clients cannot mint their
 * own folders.
 */
export const UPLOAD_FOLDERS = {
  product: 'product',
  restaurant: 'restaurant',
  banners: 'banners',
} as const;

export type UploadFolder = (typeof UPLOAD_FOLDERS)[keyof typeof UPLOAD_FOLDERS];

/** Raised when object storage rejects the signed PUT (step 2 of the flow). */
export class UploadFailedError extends Error {
  readonly status: number;
  readonly statusText: string;

  constructor(status: number, statusText: string) {
    super(`Upload failed: ${status} ${statusText}`);
    this.name = 'UploadFailedError';
    this.status = status;
    this.statusText = statusText;
  }
}

export const uploadApi = {
  /**
   * Step 1 — ask the gateway for `count` signed PUT slots (1 to
   * {@link PRESIGN_MAX_BATCH}) in one folder. The 2026-08-18 contract names no
   * files: slots are interchangeable, each carrying its own storage `key`, and
   * the caller assigns them to files. `bucket` is always {@link UPLOAD_BUCKET}
   * for now.
   *
   * Answers a `{ list, meta }` envelope; a slot may still carry its own
   * `error`, so callers inspect slots rather than trusting the HTTP status.
   */
  presign(count: number, folder: UploadFolder): Promise<ListResult<PresignResponse>> {
    return postList(
      'uploads/presign',
      { count, bucket: UPLOAD_BUCKET, folder },
      presignResponseSchema,
    );
  },

  /**
   * Step 2 — PUT the bytes straight to object storage.
   *
   * Deliberately uses bare `fetch`, not `@noor/api`'s `http`: the presigned URL
   * is absolute and self-authenticating, so the client's base URL and
   * `Authorization` header must not be applied — S3-compatible storage rejects
   * a signed request that carries an unexpected auth header. `Content-Type` is
   * sent because the signature covers it.
   */
  async putFile(uploadUrl: string, file: File, signal?: AbortSignal): Promise<void> {
    const response = await fetch(uploadUrl, {
      method: 'PUT',
      body: file,
      headers: { 'Content-Type': file.type || 'application/octet-stream' },
      ...(signal ? { signal } : {}),
    });
    if (!response.ok) {
      throw new UploadFailedError(response.status, response.statusText);
    }
  },

  /**
   * The whole flow: presign, PUT, and hand back the storage `key` to persist on
   * the owning record. This is what feature code should call.
   *
   * The KEY is what the write endpoints take — not `public_url`. The key is the
   * durable identity of the object; the public URL is a rendering of it that
   * depends on the bucket's host, so persisting the URL would bake today's S3
   * hostname into every stored row. The backend expands the key back to a full
   * URL on read, which is why the read schemas and every `<img src>` still see
   * an absolute URL and need no base-URL joining.
   */
  async upload(file: File, folder: UploadFolder, signal?: AbortSignal): Promise<string> {
    const [key] = await uploadApi.uploadMany([file], folder, signal);
    if (key === undefined) {
      throw new UploadFailedError(0, 'presign returned no slot for the file');
    }
    return key;
  },

  /**
   * The whole flow for a gallery: ONE presign call for every file, then the
   * PUTs concurrently. Returns the storage keys in the caller's file order.
   *
   * Batching matters beyond saving round-trips — a gallery of N photos used to
   * mean N presign calls, and a partial failure left some uploaded and some
   * not with no way to tell which. Here the whole batch is presigned before any
   * byte moves, so a rejected batch (over the limit, bad folder) fails before
   * touching storage.
   *
   * Files beyond {@link PRESIGN_MAX_BATCH} are presigned in successive chunks
   * rather than rejected: the cap is the gateway's per-call limit, not a limit
   * on how many photos a product may have.
   *
   * Slots pair to files POSITIONALLY. That is sound under the count contract
   * because the request names no files — slots are interchangeable, so there is
   * no "wrong" assignment, only the need for one slot per file. A short list, a
   * slot carrying an `error`, or one missing its `upload_url`/`key` fails the
   * batch: a caller that saved a partially-uploaded gallery would persist a
   * product with silently missing photos.
   */
  async uploadMany(
    files: File[],
    folder: UploadFolder,
    signal?: AbortSignal,
  ): Promise<string[]> {
    if (files.length === 0) return [];

    const keys: string[] = [];
    for (let start = 0; start < files.length; start += PRESIGN_MAX_BATCH) {
      const chunk = files.slice(start, start + PRESIGN_MAX_BATCH);
      const { list } = await uploadApi.presign(chunk.length, folder);

      const resolved = chunk.map((file, index) => {
        const slot = list[index];
        if (!slot)
          throw new UploadFailedError(0, `presign returned no slot for ${index}`);
        if (slot.error) throw new UploadFailedError(0, slot.error);
        if (!slot.upload_url) throw new UploadFailedError(0, 'presign returned no url');
        if (!slot.key) throw new UploadFailedError(0, 'presign returned no key');
        return { file, slot };
      });

      await Promise.all(
        resolved.map(({ file, slot }) =>
          uploadApi.putFile(slot.upload_url, file, signal),
        ),
      );
      keys.push(...resolved.map(({ slot }) => slot.key));
    }
    return keys;
  },
};
